import type { CancellationRule, DepositRule, NoShowRule, PolicyRule } from '@salonomy/types';

/**
 * Client-side mirror of the API's plain-English policy renderer (apps/api/src/modules/policy/
 * service.ts → renderPolicyText). Kept byte-for-byte in step with the server so the LIVE preview in
 * the editor shows EXACTLY the text the API will freeze into a policy_version on save — and exactly
 * what a client is shown before they commit. If the server copy changes, change this too (the same
 * sentences, expressed twice: once for instant local preview, once as the persisted source of
 * truth).
 *
 * Honesty brand position (FOUNDATION + brief 07): UK English, plain, NO ASTERISKS, no hedging. The
 * text is generated from the SAME structured rule that later gets enforced, so the words can never
 * drift from the money.
 */

/** £ formatting for plain-English text: 5000 → "£50", 5050 → "£50.50". UK, no trailing ".00". */
export function formatPence(pence: number): string {
  const whole = Math.trunc(pence / 100);
  const rem = Math.abs(pence % 100);
  return rem === 0 ? `£${whole}` : `£${whole}.${rem.toString().padStart(2, '0')}`;
}

/** Plural-aware "N hour(s)". */
function hours(n: number): string {
  return n === 1 ? '1 hour' : `${n} hours`;
}

/** One plain-English sentence describing the deposit rule. UK, no asterisks. */
export function renderDepositText(rule: DepositRule): string {
  if (rule.kind === 'none' || rule.when === 'never') {
    return 'No deposit is required to book.';
  }
  const amount =
    rule.kind === 'fixed' ? formatPence(rule.amountPence) : `${rule.percent}% of the service price`;
  switch (rule.when) {
    case 'new_clients':
      return `A ${amount} deposit is required for new clients to secure this booking.`;
    case 'over_threshold':
      return `A ${amount} deposit is required for services priced at ${formatPence(
        rule.overThresholdPence,
      )} or more.`;
    case 'always':
    default:
      return `A ${amount} deposit is required to secure this booking.`;
  }
}

/** One plain-English sentence describing the cancellation rule. UK, no asterisks. */
export function renderCancellationText(rule: CancellationRule): string {
  const free = `You can cancel free of charge up to ${hours(
    rule.freeUntilHours,
  )} before your appointment.`;
  switch (rule.feeKind) {
    case 'forfeit_deposit':
      return `${free} Cancelling later means your deposit is not refunded.`;
    case 'fixed':
      return `${free} Cancelling later incurs a ${formatPence(rule.amountPence)} fee.`;
    case 'percent':
      return `${free} Cancelling later incurs a fee of ${rule.percent}% of the service price.`;
    case 'none':
    default:
      return `${free} There is no fee for cancelling after that.`;
  }
}

/** One plain-English sentence describing the no-show rule. UK, no asterisks. */
export function renderNoShowText(rule: NoShowRule): string {
  switch (rule.feeKind) {
    case 'forfeit_deposit':
      return 'If you do not show up, your deposit is not refunded.';
    case 'fixed':
      return `If you do not show up, a ${formatPence(rule.amountPence)} no-show fee applies.`;
    case 'percent':
      return `If you do not show up, a no-show fee of ${rule.percent}% of the service price applies.`;
    case 'none':
    default:
      return 'There is no fee for a missed appointment, but please let us know if your plans change.';
  }
}

/**
 * The full plain-English policy a client sees, assembled from the structured rule. The three
 * sentences joined, plus the parts separately so a surface can lay them out however it likes.
 */
export function renderPolicyText(rule: PolicyRule): {
  text: string;
  depositText: string;
  cancellationText: string;
  noShowText: string;
} {
  const depositText = renderDepositText(rule.deposit);
  const cancellationText = renderCancellationText(rule.cancellation);
  const noShowText = renderNoShowText(rule.noShow);
  return {
    depositText,
    cancellationText,
    noShowText,
    text: [depositText, cancellationText, noShowText].join(' '),
  };
}

/** The sane default rule a salon starts with: no deposit, free cancellation, no no-show fee. */
export function defaultPolicyRule(): PolicyRule {
  return {
    deposit: { kind: 'none', amountPence: 0, percent: 0, when: 'always', overThresholdPence: 0 },
    cancellation: { freeUntilHours: 24, feeKind: 'none', amountPence: 0, percent: 0 },
    noShow: { feeKind: 'none', amountPence: 0, percent: 0 },
    armAutoCharge: false,
  };
}

// ── £↔pence input conversion (mirrors ServiceModal) ────────────────────────────────

/** "1400" (pence) → "14.00" for a £ input; null → "". */
export function penceToPounds(pence: number | null | undefined): string {
  if (pence == null) return '';
  return (pence / 100).toFixed(2);
}

/** "14" | "14.5" | "£14.00" → 1400 pence; null if not a valid non-negative amount. */
export function poundsToPence(input: string): number | null {
  const cleaned = input.replace(/[£,\s]/g, '');
  if (!cleaned) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
}
